import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { authService } from './services/api';

// Components
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import BookingsList from './components/BookingsList';
import BookingDetail from './components/BookingDetail';
import BookingForm from './components/BookingForm';
import DJBookingsList from './components/DJBookingsList';
import DJBookingDetail from './components/DJBookingDetail';
import DJBookingForm from './components/DJBookingForm';
import LivemusikBookingsList from './components/LivemusikBookingsList';
import LivemusikBookingDetail from './components/LivemusikBookingDetail';
import LivemusikBookingForm from './components/LivemusikBookingForm';
import CustomersList from './components/CustomersList';
import CustomerDetail from './components/CustomerDetail';
import Calendar from './components/Calendar';
import Layout from './components/Layout';
import AnfragenList from './components/AnfragenList';
import AnfrageDetail from './components/AnfrageDetail';

// Styles
import './styles/global.css';

// Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

// Protected Route Component
function ProtectedRoute({ children }) {
  const isAuthenticated = authService.isAuthenticated();
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Navigate to="/dashboard" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Feierpakete Routes */}
          <Route
            path="/feierpakete"
            element={
              <ProtectedRoute>
                <Layout>
                  <BookingsList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/feierpakete/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <BookingForm />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/feierpakete/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <BookingDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/feierpakete/:id/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <BookingForm />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Alte Bookings Routes weiterleiten */}
          <Route path="/bookings" element={<Navigate to="/feierpakete" />} />
          <Route path="/bookings/new" element={<Navigate to="/feierpakete/new" />} />
          <Route path="/bookings/:id" element={<Navigate to="/feierpakete/:id" />} />

          {/* Placeholder Routes */}
          <Route
            path="/dj"
            element={
              <ProtectedRoute>
                <Layout>
                  <DJBookingsList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dj/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <DJBookingForm />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dj/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <DJBookingDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dj/:id/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <DJBookingForm />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Livemusik Routes */}
          <Route
            path="/livemusik"
            element={
              <ProtectedRoute>
                <Layout>
                  <LivemusikBookingsList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/livemusik/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <LivemusikBookingForm />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/livemusik/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <LivemusikBookingDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/livemusik/:id/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <LivemusikBookingForm />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/livemusik"
            element={
              <ProtectedRoute>
                <Layout>
                  <div style={{ padding: '2rem' }}>
                    <h1>Livemusik</h1>
                    <p>Verwaltung der Livemusik-Buchungen - wird noch implementiert.</p>
                  </div>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/kalender"
            element={
              <ProtectedRoute>
                <Layout>
                  <Calendar />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Kunden Routes */}
          <Route
            path="/kunden"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomersList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/kunden/:email"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Anfragen-Pipeline (neue Website-Anfragen) */}
          <Route
            path="/anfragen"
            element={
              <ProtectedRoute>
                <Layout>
                  <AnfragenList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/anfragen/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <AnfrageDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/inquiries" element={<Navigate to="/anfragen" />} />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Layout>
                  <div style={{ padding: '2rem' }}>
                    <h1>Benutzerverwaltung</h1>
                    <p>Diese Seite wird noch implementiert (nur für Admins).</p>
                  </div>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* 404 */}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
